import { useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Avi } from './Avi';
import { Popover, MenuItem, MenuDivider } from './Popover';
import { NavIcon, ChevronIcon } from './navIcons';
import { ActiveCatalogCard } from '../catalog/ActiveCatalogCard';
import { SidebarResizeHandle } from './SidebarResizeHandle';
import { darkTheme, GRAD } from '../../theme/tokens';
import { useAppStore } from '../../store/appStore';
import { useAuth } from '../../hooks/useAuth';
import { isCanvasWorkspaceRoute } from './canvasWorkspaceRoute';
import { SupportGrantList } from '../support/SupportGrantList';

const t = darkTheme; // sidebar is always dark

interface NavItem {
  id: string;
  label: string;
  path: string;
}

// Org-level pages (Connections, Team, Settings) — not part of any one catalog, so
// they get their own sidebar group outside the catalog workspace. Every member
// sees all three; each page gates its own actions (SC-3227 for Team).
//
// History: the sidebar used to carry a "Products" view item here, which on these
// pages read as a stray link (it isn't about the org page you're on). The catalog
// is reached by clicking the catalog card above the nav (SC-3512; a separate
// "Back to <catalog>" entry did that job before, SC-3444). Chat, Changes and
// Catalog Profile left the rail earlier (SC-2716, SC-2445) — they live in the
// catalog workspace.
const ORG_ITEMS: NavItem[] = [
  { id: 'connections', label: 'Connections', path: '/connections' },
  { id: 'team', label: 'Team', path: '/team' },
  { id: 'settings', label: 'Settings', path: '/settings' },
];

export function SidebarNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, org, logout, isEkomAdmin } = useAuth();
  // Collapsed icon-rail mode (SC-2393). Persisted preference; the sidebar is in
  // normal flex flow so `<main>` reflows automatically when the width changes.
  const userCollapsed = useAppStore((s) => s.sidebarCollapsed);
  const toggleSidebar = useAppStore((s) => s.toggleSidebar);
  // Expanded width is user-resizable (SC-3491) so long catalog names get room;
  // the collapsed rail stays a fixed 56px. `resizing` drops the width transition
  // mid-drag so the edge tracks the cursor instead of easing behind it.
  const expandedWidth = useAppStore((s) => s.sidebarWidth);
  const [resizing, setResizing] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);

  // In the canvas workspace the catalog's section nav lives as top tabs
  // (CanvasViewTabs), so the app sidebar becomes the "catalog + account" rail and
  // sheds the view items that would duplicate them. It stays user-collapsible
  // there — default collapsed (SC-2716; login lands in the workspace, so the rail
  // must expand on demand to reveal the catalog name + picker). Collapse is a
  // single persisted pref honored on every route (SC-2669 previously locked the
  // workspace rail to 56px with no toggle; SC-2716 reverses that).
  const inCanvasWorkspace = isCanvasWorkspaceRoute(location.pathname);
  const collapsed = userCollapsed;

  // In the workspace the rail carries only the catalog switcher + account (the
  // section tabs and the permanent ChatPanel cover the rest). Elsewhere — the
  // org-level pages, the upload page, a 404 — it adds the org pages, and the
  // catalog card becomes the way back into the active catalog (SC-3512).
  const orgItems = inCanvasWorkspace ? [] : ORG_ITEMS;

  const initial = user?.name?.charAt(0)?.toUpperCase() ?? 'U';
  const displayName = user?.name ?? 'User';
  const displayOrg = org?.name ?? '';

  const userAvatar = (
    <div style={{
      width: 28, height: 28, borderRadius: '50%',
      background: GRAD, display: 'flex', alignItems: 'center',
      justifyContent: 'center', fontSize: 11, fontWeight: 600, color: '#fff',
      flexShrink: 0,
    }}>
      {initial}
    </div>
  );

  // Collapsed: just the avatar (the popover still opens the account menu).
  // Expanded: avatar + name + org.
  const userTrigger = collapsed ? (
    <div
      title={displayName}
      aria-label={`Account: ${displayName}`}
      style={{ display: 'flex', justifyContent: 'center', padding: '8px 0', borderRadius: 7 }}
    >
      {userAvatar}
    </div>
  ) : (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '8px 10px', borderRadius: 7,
        transition: 'background 0.12s',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = t.sbHover; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
    >
      {userAvatar}
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{
          fontSize: 12, fontWeight: 500, color: t.sbActive,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{displayName}</div>
        {displayOrg && (
          <div style={{
            fontSize: 10, color: t.sbText,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{displayOrg}</div>
        )}
      </div>
    </div>
  );

  const width = collapsed ? 56 : expandedWidth;

  return (
    <div
      ref={sidebarRef}
      style={{
        position: 'relative',
        width, minWidth: width, background: t.sb,
        display: 'flex', flexDirection: 'column',
        borderRight: `1px solid ${t.sbBorder}`, overflow: 'visible', height: '100%',
        transition: resizing ? 'none' : 'width 0.15s ease',
      }}
    >
      {!collapsed && (
        <SidebarResizeHandle
          measureLeftEdge={() => sidebarRef.current?.getBoundingClientRect().left ?? 0}
          onResizingChange={setResizing}
        />
      )}
      {/* Logo + collapse/expand toggle. The toggle shows on every route now,
          including the canvas workspace, so the rail can expand to reveal the
          catalog name + picker (SC-2716). */}
      {collapsed ? (
        <div style={{ padding: '16px 0 10px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
          <Avi size={30} />
          <SidebarToggle collapsed onClick={toggleSidebar} />
        </div>
      ) : (
        <div style={{ padding: '18px 18px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Avi size={30} />
            <span style={{ fontSize: 16, fontWeight: 600, color: '#fff', letterSpacing: '-0.02em', flex: 1 }}>
              EKOM
            </span>
            <SidebarToggle collapsed={false} onClick={toggleSidebar} />
          </div>
        </div>
      )}

      {/* Active catalog card — a compact avatar in the rail (click for the
          switch / rename / new-catalog popover). */}
      <ActiveCatalogCard collapsed={collapsed} />

      {/* Navigation */}
      <nav
        aria-label="Main"
        style={{ padding: collapsed ? '16px 6px 8px' : '16px 8px 8px', flex: 1, borderTop: `1px solid ${t.sbBorder}` }}
      >
        {orgItems.length > 0 && (
          <div role="group" aria-label="Organization">
            {!collapsed && (
              <div
                aria-hidden
                style={{
                  fontSize: 10, fontWeight: 600, letterSpacing: '0.08em',
                  color: t.sbText, padding: '0 10px 6px',
                }}
              >
                ORGANIZATION
              </div>
            )}
            {orgItems.map((item) => (
              <NavButton
                key={item.id}
                item={item}
                collapsed={collapsed}
                active={location.pathname === item.path || location.pathname.startsWith(`${item.path}/`)}
                onClick={() => navigate(item.path)}
              />
            ))}
          </div>
        )}
      </nav>

      {/* User menu */}
      <div style={{ padding: collapsed ? '8px 6px 12px' : '8px 8px 12px', borderTop: `1px solid ${t.sbBorder}` }}>
        <Popover trigger={userTrigger} align="top-start">
          {user?.email && (
            <div style={{
              padding: '8px 12px 6px',
              fontSize: 11, color: t.text3,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {user.email}
            </div>
          )}
          <MenuDivider />
          {/* Connections (SC-3122) is org-level like Team, but NOT gated: every
              member may see where the org's data goes — the page gates its own
              affordances (sources, settings, the kill switch) per permission. */}
          <MenuItem onClick={() => navigate('/connections')}>Connections</MenuItem>
          {/* Team is org-level too, and every member sees the roster (SC-3227);
              the page gates invite and title edit itself. */}
          <MenuItem onClick={() => navigate('/team')}>Team</MenuItem>
          <MenuItem onClick={() => navigate('/settings')}>Settings</MenuItem>
          {/* Staff org switcher (SC-2948) — EKOM staff only; mounts (and so
              fetches) only while the menu is open. */}
          {isEkomAdmin && (
            <>
              <MenuDivider />
              <SupportGrantList variant="menu" />
            </>
          )}
          <MenuDivider />
          <MenuItem onClick={() => { void logout(); }} variant="danger">Sign out</MenuItem>
        </Popover>
      </div>
    </div>
  );
}

/** One sidebar entry. In the collapsed rail the label is gone, so the icon carries
 *  the name via title (hover tooltip) + aria-label (AT). */
function NavButton({
  item,
  collapsed,
  active,
  onClick,
}: {
  item: NavItem;
  collapsed: boolean;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      aria-label={collapsed ? item.label : undefined}
      title={collapsed ? item.label : undefined}
      style={{
        display: 'flex', alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'flex-start',
        gap: 10,
        padding: collapsed ? '9px 0' : '8px 10px',
        borderRadius: 7, marginBottom: 1,
        cursor: 'pointer',
        background: active ? t.sbAccentBg : 'transparent',
        border: 'none',
        width: '100%',
        textAlign: 'left',
        fontFamily: 'inherit',
        // The button color drives both the (currentColor) icon and the label.
        color: active ? t.sbActive : t.sbText,
      }}
    >
      <span style={{ display: 'inline-flex', flexShrink: 0 }}>
        <NavIcon id={item.id} />
      </span>
      {!collapsed && (
        <span
          style={{
            fontSize: 13, fontWeight: active ? 500 : 400, flex: 1, color: 'inherit',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}
        >
          {item.label}
        </span>
      )}
    </button>
  );
}

/** The sidebar collapse/expand chevron button. */
function SidebarToggle({ collapsed, onClick }: { collapsed: boolean; onClick: () => void }) {
  const label = collapsed ? 'Expand sidebar' : 'Collapse sidebar';
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 28, height: 28, borderRadius: 7,
        background: 'transparent', border: 'none', color: t.sbText,
        cursor: 'pointer', flexShrink: 0,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = t.sbHover; e.currentTarget.style.color = t.sbActive; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = t.sbText; }}
    >
      <ChevronIcon direction={collapsed ? 'right' : 'left'} />
    </button>
  );
}
